import React, { useState, useEffect } from 'react';
import translations from './HealthInsuranceFormTranslations';
import "../css/TermInsuranceForm.css";
import { submitHealthInsuranceForm, getAgentIdByToken } from '../services/api';
import Modal from '../components/Modal';

const HealthInsuranceForm = () => {
    const [language] = useState('en');
    const t = translations[language];
    const [agentId, setAgentId] = useState(null);
    const [loadingAgent, setLoadingAgent] = useState(true);
    const [agentError, setAgentError] = useState('');

    // Define Tier 1 cities
    const TIER_1_CITIES = [
        'Delhi', 'Mumbai', 'Chennai', 'Kolkata', 
        'Bengaluru', 'Hyderabad', 'Pune', 'Ahmedabad'
    ];

    const initialFormState = {
        form_timestamp: new Date().toISOString().slice(0, 19).replace('T', ' '),
        email: '',
        name: '',
        mobile: '',
        city_of_residence: '',
        age: '',
        number_of_members: '',
        eldest_member_age: '',
        pre_existing_diseases: '',
        major_surgery: '',
        existing_insurance: '',
        current_coverage: null,
        port_policy: '',
        tier_city: 'Others'
    };

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [showCoverageField, setShowCoverageField] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch agent ID from backend using token in URL
    useEffect(() => {
        const fetchAgentId = async () => {
            try {
                // Extract token from URL path
                const pathParts = window.location.pathname.split('/');
                const token = pathParts[pathParts.length - 1];
                
                if (token) {
                    const response = await getAgentIdByToken(token);
                    console.log("==response health ==",response.data)

                    setAgentId(response.data);
                } else {
                    // Fallback to localStorage if token not found
                    const storedAgentId = localStorage.getItem("agentId");
                    if (storedAgentId) {
                        setAgentId(parseInt(storedAgentId, 10));
                    } else {
                        setAgentError("Agent information could not be loaded");
                    }
                }
            } catch (error) {
                console.error('Error fetching agent ID:', error);
                setAgentError("Failed to load agent information");
                
                // Fallback to localStorage
                const storedAgentId = localStorage.getItem("agentId");
                if (storedAgentId) {
                    setAgentId(parseInt(storedAgentId, 10));
                }
            } finally {
                setLoadingAgent(false);
            }
        };

        fetchAgentId();
    }, []);

    const resetForm = () => {
        setFormData(initialFormState);
        setErrors({});
        setShowCoverageField(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            let updatedValue = value;
            let updatedTierCity = prev.tier_city;

            // Handle existing_insurance logic
            if (name === 'existing_insurance') {
                const isExisting = value === 'Yes';
                setShowCoverageField(isExisting);
                if (!isExisting) {
                    setErrors(prev => ({ ...prev, current_coverage: undefined }));
                    return {
                        ...prev,
                        existing_insurance: value,
                        current_coverage: null,
                        tier_city: updatedTierCity
                    };
                }
            }

            // Convert numeric inputs
            if (['age', 'number_of_members', 'eldest_member_age'].includes(name)) {
                updatedValue = value === '' ? '' : parseInt(value, 10);
            } else if (name === 'current_coverage') {
                updatedValue = value === '' ? null : parseInt(value, 10);
            }

            // Update tier city when city changes
            if (name === 'city_of_residence') {
                updatedTierCity = TIER_1_CITIES.includes(value) ? 'Tier 1' : 'Others';
            }

            // Clear errors
            if (errors[name]) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            }

            return {
                ...prev,
                [name]: updatedValue,
                tier_city: updatedTierCity
            };
        });
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Personal Details Validation
        if (!formData.email) {
            newErrors.email = t.errors.emailRequired;
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t.errors.invalidEmail;
            isValid = false;
        }

        if (!formData.name.trim()) {
            newErrors.name = t.errors.nameRequired;
            isValid = false;
        }

        if (!formData.mobile) {
            newErrors.mobile = t.errors.mobileRequired;
            isValid = false;
        } else if (!/^\d{10}$/.test(formData.mobile)) {
            newErrors.mobile = t.errors.invalidMobile;
            isValid = false;
        }

        if (!formData.city_of_residence.trim()) {
            newErrors.city_of_residence = t.errors.cityRequired;
            isValid = false;
        }

        if (formData.age === '' || isNaN(formData.age)) {
            newErrors.age = t.errors.ageRequired;
            isValid = false;
        } else if (formData.age < 18) {
            newErrors.age = t.errors.invalidAge;
            isValid = false;
        }

        // Family Details Validation
        if (formData.number_of_members === '' || isNaN(formData.number_of_members)) {
            newErrors.number_of_members = t.errors.membersRequired;
            isValid = false;
        } else if (formData.number_of_members < 1 || formData.number_of_members > 5) {
            newErrors.number_of_members = t.errors.invalidMembers;
            isValid = false;
        }

        if (formData.eldest_member_age === '' || isNaN(formData.eldest_member_age)) {
            newErrors.eldest_member_age = t.errors.eldestAgeRequired;
            isValid = false;
        } else if (formData.eldest_member_age < formData.age) {
            newErrors.eldest_member_age = t.errors.eldestAgeValidation;
            isValid = false;
        }

        // Medical History Validation
        if (!formData.pre_existing_diseases) {
            newErrors.pre_existing_diseases = t.errors.preExistingRequired;
            isValid = false;
        }

        if (!formData.major_surgery) {
            newErrors.major_surgery = t.errors.surgeryRequired;
            isValid = false;
        }

        // Insurance Details Validation
        if (!formData.existing_insurance) {
            newErrors.existing_insurance = t.errors.insuranceRequired;
            isValid = false;
        } else if (formData.existing_insurance === 'Yes' && 
                  (formData.current_coverage === null || formData.current_coverage === '')) {
            newErrors.current_coverage = t.errors.coverageRequired;
            isValid = false;
        } else if (formData.existing_insurance === 'Yes' && formData.current_coverage < 300000) {
            newErrors.current_coverage = t.errors.invalidCoverage;
            isValid = false;
        }

        if (!formData.port_policy) {
            newErrors.port_policy = t.errors.portPolicyRequired;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check agent ID before form validation
        if (!agentId) {
            setAgentError("Agent information is required to submit the form");
            return;
        }
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const dataToSend = {
                ...formData,
                form_timestamp: new Date(formData.form_timestamp).toISOString().slice(0, 19).replace('T', ' '),
                agent_id: agentId,
                tier_city: formData.tier_city
            };

            await submitHealthInsuranceForm(dataToSend);
            setShowModal(true);

        } catch (error) {
            console.error('Submission error:', error);
            alert(`Error: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Show loading state while fetching agent ID
    if (loadingAgent) {
        return (
            <div className="term-insurance-form-container">
                <div className="loading-message">
                    <p>Loading agent information...</p>
                </div>
            </div>
        );
    }

    // Show error if agent ID couldn't be determined
    if (!agentId) {
        return (
            <div className="term-insurance-form-container">
                <div className="error-message">
                    <h2>Agent Information Missing</h2>
                    <p>{agentError || "Please use a valid form link"}</p>
                    <p>If you're an agent, please try logging in again.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="term-insurance-form-container">
            <div className="term-insurance-form">
                <h1>{t.labels.title}</h1>
                <p style={{ color: 'rgb(112, 16, 99)' }}>
                    {t.labels.description}
                </p>

                <form onSubmit={handleSubmit} className="form-section">
                <div className="form-group">
                            <label htmlFor="form_timestamp">{t.labels.form_timestamp}</label>
                            <input
                            type="text"
                            name="form_timestamp"
                            value={formData.form_timestamp}
                            readOnly
                            />
                       </div>
                    {/* Personal Details Section */}
                    <fieldset>
                        <legend>{t.labels.personalDetails}</legend>
                        
                        {/* Email */}
                        <div className="form-group">
                            <label htmlFor="email">{t.labels.email}</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder={t.placeholders.email}
                                required
                            />
                            {errors.email && <span className="error-message">{errors.email}</span>}
                        </div>

                        {/* Name */}
                        <div className="form-group">
                            <label htmlFor="name">{t.labels.name}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={t.placeholders.name}
                                required
                            />
                            {errors.name && <span className="error-message">{errors.name}</span>}
                        </div>

                        {/* Mobile */}
                        <div className="form-group">
                            <label htmlFor="mobile">{t.labels.mobile}</label>
                            <input
                                type="tel"
                                id="mobile"
                                name="mobile"
                                value={formData.mobile}
                                onChange={handleChange}
                                placeholder={t.placeholders.mobile}
                                required
                            />
                            {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                        </div>

                        {/* City of Residence */}
                        <div className="form-group">
                            <label htmlFor="city_of_residence">{t.labels.city_of_residence}</label>
                            <select
                                id="city_of_residence"
                                name="city_of_residence"
                                value={formData.city_of_residence}
                                onChange={handleChange}
                                required
                            >
                                <option value="">{t.labels.selectOption}</option>
                                {t.data.cityOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {errors.city_of_residence && 
                                <span className="error-message">{errors.city_of_residence}</span>}
                        </div>

                        {/* Age */}
                        <div className="form-group">
                            <label htmlFor="age">{t.labels.age}</label>
                            <input
                                type="text"
                                id="age"
                                name="age"
                                value={formData.age}
                                onChange={handleChange}
                                placeholder={t.placeholders.age}
                                min="18"
                                required
                                pattern="[0-9]*" 
                                inputMode="numeric" 
                            />
                            {errors.age && <span className="error-message">{errors.age}</span>}
                        </div>

                        {/* Tier City (read-only) */}
                        <div className="form-group">
                            <label htmlFor="tier_city">City Tier</label>
                            <input
                                type="text"
                                id="tier_city"
                                name="tier_city"
                                value={formData.tier_city}
                                readOnly
                            />
                        </div>
                    </fieldset>

                    {/* Family Details Section */}
                    <fieldset>
                        <legend>{t.labels.familyDetails}</legend>
                        
                        {/* Number of Members */}
                        <div className="form-group">
                            <label htmlFor="number_of_members">{t.labels.numberOfMembers}</label>
                            <input
                                type="text"
                                id="number_of_members"
                                name="number_of_members"
                                value={formData.number_of_members}
                                onChange={handleChange}
                                placeholder={t.placeholders.numberOfMembers}
                                min="1"
                                max="5"
                                required
                                pattern="[0-9]*" 
                                inputMode="numeric" 
                            />
                            {errors.number_of_members && 
                                <span className="error-message">{errors.number_of_members}</span>}
                        </div>

                        {/* Eldest Member Age */}
                        <div className="form-group">
                            <label htmlFor="eldest_member_age">{t.labels.eldestMemberAge}</label>
                            <input
                                type="text"
                                id="eldest_member_age"
                                name="eldest_member_age"
                                value={formData.eldest_member_age}
                                onChange={handleChange}
                                placeholder={t.placeholders.eldestMemberAge}
                                required
                                pattern="[0-9]*" 
                                inputMode="numeric" 
                            />
                            {errors.eldest_member_age && 
                                <span className="error-message">{errors.eldest_member_age}</span>}
                        </div>
                    </fieldset>

                    {/* Medical History Section */}
                    <fieldset>
                        <legend>{t.labels.medicalHistory}</legend>
                        
                        {/* Pre-existing Diseases */}
                        <div className="form-group">
                            <label>{t.labels.preExistingDiseases}</label>
                            <div className="option-pills">
                                {t.data.yesNoOptions.map(option => (
                                    <label key={option.value} className="pill">
                                        <input
                                            type="radio"
                                            name="pre_existing_diseases"
                                            value={option.value}
                                            checked={formData.pre_existing_diseases === option.value}
                                            onChange={handleChange}
                                            required
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.pre_existing_diseases && 
                                <span className="error-message">{errors.pre_existing_diseases}</span>}
                        </div>

                        {/* Major Surgery */}
                        <div className="form-group">
                            <label>{t.labels.majorSurgery}</label>
                            <div className="option-pills">
                                {t.data.yesNoOptions.map(option => (
                                    <label key={option.value} className="pill">
                                        <input
                                            type="radio"
                                            name="major_surgery"
                                            value={option.value}
                                            checked={formData.major_surgery === option.value}
                                            onChange={handleChange}
                                            required
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.major_surgery && 
                                <span className="error-message">{errors.major_surgery}</span>}
                        </div>
                    </fieldset>

                    {/* Insurance Details Section */}
                    <fieldset>
                        <legend>{t.labels.insuranceDetails}</legend>
                        
                        {/* Existing Insurance */}
                        <div className="form-group">
                            <label>{t.labels.existingInsurance}</label>
                            <div className="option-pills">
                                {t.data.yesNoOptions.map(option => (
                                    <label key={option.value} className="pill">
                                        <input
                                            type="radio"
                                            name="existing_insurance"
                                            value={option.value}
                                            checked={formData.existing_insurance === option.value}
                                            onChange={handleChange}
                                            required
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.existing_insurance && 
                                <span className="error-message">{errors.existing_insurance}</span>}
                        </div>

                        {/* Current Coverage (conditional) */}
                        {showCoverageField && (
                            <div className="form-group">
                                <label htmlFor="current_coverage">{t.labels.currentCoverage}</label>
                                <input
                                    type="text"
                                    id="current_coverage"
                                    name="current_coverage"
                                    value={formData.current_coverage || ''}
                                    onChange={handleChange}
                                    placeholder={t.placeholders.currentCoverage}
                                    min="300000"
                                    required
                                    pattern="[0-9]*" 
                                    inputMode="numeric" 
                                />
                                {errors.current_coverage && 
                                    <span className="error-message">{errors.current_coverage}</span>}
                            </div>
                        )}

                        {/* Port Policy */}
                        <div className="form-group">
                            <label>{t.labels.portPolicy}</label>
                            <div className="option-pills">
                                {t.data.yesNoOptions.map(option => (
                                    <label key={option.value} className="pill">
                                        <input
                                            type="radio"
                                            name="port_policy"
                                            value={option.value}
                                            checked={formData.port_policy === option.value}
                                            onChange={handleChange}
                                            required
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.port_policy && 
                                <span className="error-message">{errors.port_policy}</span>}
                        </div>
                    </fieldset>

                    {/* Submit Button */}
                    <button 
                        type="submit" 
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : t.labels.submit}
                    </button>
                </form>

                {/* Success Modal */}
                <Modal 
                    show={showModal} 
                    onClose={() => {
                        setShowModal(false);
                        resetForm();
                    }} >
                    <h2>🎉 Form Submitted Successfully!</h2>
                    <p>Thank you for your response.</p>
                </Modal>
            </div>
        </div>
    );
};

export default HealthInsuranceForm;